import { Injectable } from "@angular/core";

import { forkJoin, Observable, of, throwError } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";

import { DffError } from "@dffedb/util";

import { Forbrug } from "../model/forbrug.model";
import { SammenlignForbrugIndstillinger } from "../model/sammenlign-forbrug-indstillinger.model";
import { SammenlignForbrugViewModel } from "../model/sammenlign-forbrug.viewmodel";
import { SammenlignForbrugRepository } from "./sammenlign-forbrug.repository";

@Injectable({ providedIn: "root" })
export class SammenlignForbrugService {
    constructor(private readonly repository: SammenlignForbrugRepository) {}

    public hentData(): Observable<SammenlignForbrugViewModel> {
        return this.hentIndstillinger().pipe(
            switchMap((indstillinger) => forkJoin([of(indstillinger), this.hentForbrug(indstillinger.fofScript)])),
            map(([indstillinger, forbrug]) => this.map(forbrug, indstillinger))
        );
    }

    public hentForbrug(fofScript: string): Observable<Forbrug> {
        return this.repository.hentForbrug(fofScript).pipe(catchError((e) => throwError(this.createError(e))));
    }

    public hentIndstillinger(): Observable<SammenlignForbrugIndstillinger> {
        return this.repository.hentIndstillinger().pipe(catchError((e) => throwError(this.createError(e))));
    }

    public formatterNummer(value: number): string {
        return (Math.round(value * 100) / 100).toString().replace(".", ",");
    }

    private map(forbrug: Forbrug, indstillinger: SammenlignForbrugIndstillinger): SammenlignForbrugViewModel {
        return {
            forbrug,
            sideTitel: indstillinger.menuTekst,
            sideTekst: indstillinger.topIndhold,
            grafBeskrivelse: this.hentGrafBeskrivelse(forbrug)
        };
    }

    private hentGrafBeskrivelse(forbrug: Forbrug): string {
        const dkNormtal = this.formatterNummer(forbrug.normtal);
        return forbrug.normtal > 0
            ? `<div> <span style="color: #ff851b;font-size: xx-large;margin-right: 5px;">|</span>
               Forbruget for lignende ejendomme i dit omr&aring;de er ${dkNormtal} ${forbrug.enhed}/m<sup>2</sup></div>`
            : // eslint-disable-next-line max-len
              "<strong>Datagrundlaget er for lille til at foretage en retvisende beregning af det gennemsnitlige forbrug for ejendomme i denne kategori.</strong>";
    }

    private createError(innerError: Error): DffError {
        return new DffError(innerError.message, {
            innerError,
            friendlyMessage: "Der er opstået en fejl i forbindelse med hentning af data."
        });
    }
}
